import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faMessage } from '@fortawesome/pro-regular-svg-icons/faMessage';
import { faMessagePlus } from '@fortawesome/pro-regular-svg-icons/faMessagePlus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { NITRO_CLAW_AGENT_NAME } from 'api/nitro-claw';
import { AIFillSparkles16 } from 'components/atoms/icons/ai-fill-sparkles';
import { useTranslate } from 'hooks/use-translation';
import { prependAgentConversationToHistory, useAgentChatHistoryInfiniteQuery } from 'queries/ai-chat/ai-chat';
import { hasDesktopSession } from 'services/desktop-session';
import { useDismissBootSkeleton } from 'utils/dismiss-boot-skeleton';
import { emitQuickEntryAction, focusMainWindow, hideQuickEntryWindow, listenAgentChatHistoryUpdated } from 'utils/quick-entry-native';
const TransparentRootStyle = createGlobalStyle`
  html,
  body,
  #root {
    background: transparent !important;
    background-color: transparent !important;
  }

  /* Boot skeleton / global chrome must not paint an opaque rect behind cards. */
  #rl-boot-skeleton {
    display: none !important;
  }
`;
const OverlayRoot = styled.div`
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  background: transparent;
`;
const Panel = styled.div`
  position: relative;
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
`;
/**
 * Elevation for transparent macOS panels: avoid CSS `box-shadow` (WKWebView
 * composites it as a dark fringe) and hard borders. Prefer filter drop-shadow.
 */ const elevatedSurface = `
  border: none;
  background: var(--layer-01, #f4f4f4);
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.18));
`;
const PromptBar = styled.form`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 52px;
  padding: 8px 10px 8px 16px;
  border-radius: 999px;
  ${elevatedSurface};
  background: var(--layer-01, #f4f4f4);
`;
const SparkleWrap = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  color: var(--icon-primary, #161616);
`;
const PromptInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 16px;
  line-height: 24px;
  color: var(--text-primary, #161616);

  &::placeholder {
    color: var(--text-placeholder, #a8a8a8);
  }
`;
const ChatPickerButton = styled.button`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 6px;
  max-width: 160px;
  margin: 0;
  padding: 4px 6px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  color: var(--text-secondary, #6f6f6f);
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;

  &:hover {
    background: var(--layer-hover, #e8e8e8);
  }
`;
const ChatPickerLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const ChatPickerChevron = styled.span`
  display: inline-flex;
  font-size: 10px;
  color: var(--icon-secondary, #8d8d8d);
  transform: ${({ $open })=>$open ? 'rotate(180deg)' : 'none'};
  transition: transform 120ms ease;
`;
const SendButton = styled.button`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: var(--border-radius-8, 8px);
  cursor: ${({ $active })=>$active ? 'pointer' : 'default'};
  background: ${({ $active })=>$active ? 'var(--button-primary, #161616)' : 'var(--button-disabled, #c6c6c6)'};
  color: ${({ $active })=>$active ? 'var(--icon-button-primary, #ffffff)' : 'var(--icon-on-color-disabled, #8d8d8d)'};
`;
const ChatMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  left: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 280px;
  overflow: auto;
  padding: 8px;
  border-radius: 16px;
  border: none;
  background: var(--scarlet-background-primary, #ffffff);
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.18));
`;
const ChatMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: 10px;
  background: ${({ $active })=>$active ? 'var(--layer-selected, #f2f2f2)' : 'transparent'};
  text-align: left;
  cursor: pointer;
  color: var(--text-primary, #161616);
  font-size: 14px;
  line-height: 20px;

  &:hover {
    background: var(--layer-hover, #f4f4f4);
  }
`;
const ChatMenuTitle = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const SignedOut = styled.div`
  margin: auto;
  max-width: 360px;
  padding: 16px 20px;
  border-radius: 12px;
  border: none;
  background: var(--scarlet-background-primary, #ffffff);
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.18));
  color: var(--text-secondary, #6f6f6f);
  font-size: 14px;
  line-height: 20px;
  text-align: center;
`;
const OpenAppButton = styled.button`
  margin-top: 12px;
  border: none;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
  background: var(--button-primary, #161616);
  color: var(--icon-on-color, #ffffff);
  font-size: 14px;
`;
/**
 * Floating Quick Entry UI — loaded only in the `quick-entry` Tauri window.
 */ export const QuickEntryOverlay = ()=>{
    useDismissBootSkeleton(true);
    const translate = useTranslate();
    const queryClient = useQueryClient();
    const inputRef = useRef(null);
    const panelRef = useRef(null);
    const [value, setValue] = useState('');
    const [menuOpen, setMenuOpen] = useState(false);
    const [selectedChat, setSelectedChat] = useState({
        kind: 'new'
    });
    const hasSession = hasDesktopSession();
    const { data, isLoading, refetch } = useAgentChatHistoryInfiniteQuery({
        limit: 20
    }, NITRO_CLAW_AGENT_NAME, {
        enabled: hasSession,
        staleTime: 0,
        refetchOnMount: 'always'
    });
    const conversations = useMemo(()=>{
        var _ref;
        const pages = (_ref = data === null || data === void 0 ? void 0 : data.pages) !== null && _ref !== void 0 ? _ref : [];
        return pages.flatMap((page)=>{
            var _page_conversations;
            return (_page_conversations = page.conversations) !== null && _page_conversations !== void 0 ? _page_conversations : [];
        }).slice(0, 20);
    }, [
        data === null || data === void 0 ? void 0 : data.pages
    ]);
    useEffect(()=>{
        if (!hasSession) {
            return undefined;
        }
        const focusInput = ()=>{
            var _inputRef_current, _inputRef_current1;
            (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.focus();
            (_inputRef_current1 = inputRef.current) === null || _inputRef_current1 === void 0 ? void 0 : _inputRef_current1.select();
        };
        focusInput();
        void refetch();
        const timer = window.setTimeout(focusInput, 50);
        return ()=>window.clearTimeout(timer);
    }, [
        hasSession,
        refetch
    ]);
    useEffect(()=>{
        if (!hasSession) {
            return undefined;
        }
        let cancelled = false;
        let unlisten;
        listenAgentChatHistoryUpdated((payload)=>{
            if (payload.agentName !== NITRO_CLAW_AGENT_NAME) {
                return;
            }
            prependAgentConversationToHistory(queryClient, payload.agentName, payload.conversation, {
                broadcast: false
            });
        }).then((fn)=>{
            if (cancelled) {
                fn();
                return;
            }
            unlisten = fn;
        });
        return ()=>{
            cancelled = true;
            unlisten === null || unlisten === void 0 ? void 0 : unlisten();
        };
    }, [
        hasSession,
        queryClient
    ]);
    useEffect(()=>{
        const onKeyDown = (event)=>{
            if (event.key === 'Escape') {
                event.preventDefault();
                if (menuOpen) {
                    setMenuOpen(false);
                    return;
                }
                void hideQuickEntryWindow();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return ()=>window.removeEventListener('keydown', onKeyDown);
    }, [
        menuOpen
    ]);
    useEffect(()=>{
        if (!menuOpen) {
            return undefined;
        }
        const onPointerDown = (event)=>{
            var _panelRef_current;
            const target = event.target;
            if (target && ((_panelRef_current = panelRef.current) === null || _panelRef_current === void 0 ? void 0 : _panelRef_current.contains(target))) {
                return;
            }
            setMenuOpen(false);
        };
        document.addEventListener('pointerdown', onPointerDown);
        return ()=>document.removeEventListener('pointerdown', onPointerDown);
    }, [
        menuOpen
    ]);
    useEffect(()=>{
        const onVisibility = ()=>{
            if (document.visibilityState === 'visible' && hasSession) {
                void refetch();
            }
        };
        document.addEventListener('visibilitychange', onVisibility);
        return ()=>document.removeEventListener('visibilitychange', onVisibility);
    }, [
        hasSession,
        refetch
    ]);
    const dispatchAction = useCallback(async (action)=>{
        await emitQuickEntryAction(action);
        // focus_main_window hides the overlay and keeps Nitro Claw frontmost.
        // Do NOT call hideQuickEntryWindow here — that restores the previous app.
        await focusMainWindow();
    }, []);
    const onSubmit = useCallback(async (event)=>{
        event === null || event === void 0 ? void 0 : event.preventDefault();
        const message = value.trim();
        if (!message) {
            return;
        }
        const conversationId = selectedChat.kind === 'existing' ? selectedChat.conversationId : uuidv4();
        if (selectedChat.kind === 'new') {
            prependAgentConversationToHistory(queryClient, NITRO_CLAW_AGENT_NAME, {
                conversation_id: conversationId,
                title: message.slice(0, 80)
            }, {
                broadcast: false
            });
        }
        setValue('');
        setMenuOpen(false);
        setSelectedChat({
            kind: 'new'
        });
        await dispatchAction({
            type: 'submit',
            message,
            conversationId
        });
    }, [
        dispatchAction,
        queryClient,
        selectedChat,
        value
    ]);
    const onInputKeyDown = useCallback((event)=>{
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            void onSubmit();
        }
    }, [
        onSubmit
    ]);
    const toggleMenu = useCallback((event)=>{
        event.preventDefault();
        event.stopPropagation();
        const nextOpen = !menuOpen;
        setMenuOpen(nextOpen);
        if (nextOpen) {
            void refetch();
        }
    }, [
        menuOpen,
        refetch
    ]);
    const placeholder = translate('nitro_claw.quick_entry.placeholder', undefined, 'Ask anything');
    const sendLabel = translate('nitro_claw.quick_entry.send', undefined, 'Send');
    const newChatLabel = translate('nitro_claw.quick_entry.new_chat', undefined, 'New Chat');
    const pickChatLabel = translate('nitro_claw.quick_entry.pick_chat', undefined, 'Choose chat');
    const untitled = translate('nitro_claw.sidebar.untitled_chat', undefined, 'Untitled conversation');
    const signedOutCopy = translate('nitro_claw.quick_entry.signed_out', undefined, 'Sign in to Nitro Claw to use Quick Entry.');
    const openAppLabel = translate('nitro_claw.quick_entry.open_app', undefined, 'Open Nitro Claw');
    if (!hasSession) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(TransparentRootStyle, {}),
                /*#__PURE__*/ _jsx(OverlayRoot, {
                    "data-cy": "nitro-claw-quick-entry.signed-out",
                    children: /*#__PURE__*/ _jsxs(SignedOut, {
                        children: [
                            signedOutCopy,
                            /*#__PURE__*/ _jsx("div", {
                                children: /*#__PURE__*/ _jsx(OpenAppButton, {
                                    type: "button",
                                    onClick: ()=>{
                                        void focusMainWindow();
                                    },
                                    children: openAppLabel
                                })
                            })
                        ]
                    })
                })
            ]
        });
    }
    const canSend = value.trim().length > 0;
    const pickerLabel = selectedChat.kind === 'new' ? newChatLabel : selectedChat.title;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(TransparentRootStyle, {}),
            /*#__PURE__*/ _jsx(OverlayRoot, {
                "data-cy": "nitro-claw-quick-entry",
                children: /*#__PURE__*/ _jsxs(Panel, {
                    ref: panelRef,
                    children: [
                        /*#__PURE__*/ _jsxs(PromptBar, {
                            onSubmit: (event)=>{
                                void onSubmit(event);
                            },
                            "data-cy": "nitro-claw-quick-entry.form",
                            children: [
                                /*#__PURE__*/ _jsx(SparkleWrap, {
                                    "aria-hidden": true,
                                    children: /*#__PURE__*/ _jsx(AIFillSparkles16, {})
                                }),
                                /*#__PURE__*/ _jsx(PromptInput, {
                                    ref: inputRef,
                                    value: value,
                                    onChange: (event)=>setValue(event.target.value),
                                    onKeyDown: onInputKeyDown,
                                    placeholder: placeholder,
                                    "aria-label": placeholder,
                                    "data-cy": "nitro-claw-quick-entry.input",
                                    autoComplete: "off"
                                }),
                                /*#__PURE__*/ _jsxs(ChatPickerButton, {
                                    type: "button",
                                    onMouseDown: (event)=>{
                                        // Keep input focus; avoid NSPanel blur-hide races.
                                        event.preventDefault();
                                    },
                                    onClick: toggleMenu,
                                    "aria-haspopup": "listbox",
                                    "aria-expanded": menuOpen,
                                    "aria-label": pickChatLabel,
                                    "data-cy": "nitro-claw-quick-entry.chat-picker",
                                    children: [
                                        /*#__PURE__*/ _jsx(ChatPickerLabel, {
                                            title: pickerLabel,
                                            children: pickerLabel
                                        }),
                                        /*#__PURE__*/ _jsx(ChatPickerChevron, {
                                            $open: menuOpen,
                                            "aria-hidden": true,
                                            children: /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                                                icon: faChevronDown
                                            })
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(SendButton, {
                                    type: "submit",
                                    $active: canSend,
                                    "aria-label": sendLabel,
                                    disabled: !canSend,
                                    "data-cy": "nitro-claw-quick-entry.send",
                                    children: /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                                        icon: faArrowRight
                                    })
                                })
                            ]
                        }),
                        menuOpen ? /*#__PURE__*/ _jsxs(ChatMenu, {
                            role: "listbox",
                            "aria-label": pickChatLabel,
                            "data-cy": "nitro-claw-quick-entry.chat-menu",
                            children: [
                                /*#__PURE__*/ _jsxs(ChatMenuItem, {
                                    type: "button",
                                    role: "option",
                                    $active: selectedChat.kind === 'new',
                                    "aria-selected": selectedChat.kind === 'new',
                                    onMouseDown: (event)=>event.preventDefault(),
                                    onClick: ()=>{
                                        var _inputRef_current;
                                        setSelectedChat({
                                            kind: 'new'
                                        });
                                        setMenuOpen(false);
                                        (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.focus();
                                    },
                                    "data-cy": "nitro-claw-quick-entry.new-chat",
                                    children: [
                                        /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                                            icon: faMessagePlus,
                                            "aria-hidden": true
                                        }),
                                        /*#__PURE__*/ _jsx(ChatMenuTitle, {
                                            children: newChatLabel
                                        })
                                    ]
                                }),
                                !isLoading ? conversations.map((conversation)=>{
                                    var _conversation_title;
                                    const title = ((_conversation_title = conversation.title) === null || _conversation_title === void 0 ? void 0 : _conversation_title.trim()) || untitled;
                                    const active = selectedChat.kind === 'existing' && selectedChat.conversationId === conversation.conversation_id;
                                    return /*#__PURE__*/ _jsxs(ChatMenuItem, {
                                        type: "button",
                                        role: "option",
                                        $active: active,
                                        "aria-selected": active,
                                        onMouseDown: (event)=>event.preventDefault(),
                                        onClick: ()=>{
                                            var _inputRef_current;
                                            setSelectedChat({
                                                kind: 'existing',
                                                conversationId: conversation.conversation_id,
                                                title
                                            });
                                            setMenuOpen(false);
                                            (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.focus();
                                        },
                                        "data-cy": "nitro-claw-quick-entry.recent",
                                        children: [
                                            /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                                                icon: faMessage,
                                                "aria-hidden": true
                                            }),
                                            /*#__PURE__*/ _jsx(ChatMenuTitle, {
                                                title: title,
                                                children: title
                                            })
                                        ]
                                    }, conversation.conversation_id);
                                }) : null
                            ]
                        }) : null
                    ]
                })
            })
        ]
    });
};
